import httpClient from "@/services";
import { useQuery } from "@tanstack/react-query";
import { queryClient } from "./use-query-client";
import { QueryKeys } from "@/constants";
import useUserInfo from "./use-user-info";
import { useSelector } from "react-redux";
import type { RootState } from "@/store/store";
import type { BaseResponseSummary } from "@/model/base-payload";

export type IPcpInfoPayload = {
  divisionId: string;
  groupId: string;
  loginGroupId: string;
  memberId: string;
  accessToken?: string;
  claimCoverageCode: string;
};

export type IPcpInfoResponse = {
  summary: BaseResponseSummary;
  results: Array<{
    firstName: string;
    lastName: string;
    providerId: string;
    npi: string;
    name: string;
    employeeId: string;
    middleName: string;
    visitDate: Date;
    relationship: string;
    user: string;
    memberId: string;
  }>;
};

const pcpInfoRequest = async (info: IPcpInfoPayload) => {
  const params = {
    divisionId: info.divisionId,
    groupId: info.groupId,
    loginGroupId: info.loginGroupId,
    memberId: info.memberId,
    claimCoverageCode: info.claimCoverageCode,
  };
  const config = {
    method: "POST",
    maxBodyLength: Infinity,
    url: "provider/pcpInfo",
    headers: {
      Authorization: `Bearer ${info.accessToken}`,
      "Content-Type": "application/json",
      "Accept-Version": "3.0",
    },
    data: params,
  };
  return httpClient.request(config).then((response) => response.data);
};

export const usePcpInfo = () => {
  const userInfo = useUserInfo();
  const { activeCoverage } = useSelector(
    (state: RootState) => state.planCoverageInfo,
  );

  const requestData: IPcpInfoPayload = {
    divisionId: userInfo.divisionId,
    groupId: userInfo.groupId,
    loginGroupId: userInfo.loginGroupId,
    memberId: userInfo.memberId,
    accessToken: userInfo.accessToken,
    claimCoverageCode: activeCoverage.claimCoverageCode,
  };

  const showPcpInfo = queryClient.getQueryData([
    QueryKeys.GLOBAL_FLAGS,
    userInfo.username,
  ])?.data?.[0]?.showPcpInProfile;

  return useQuery({
    queryKey: [QueryKeys.PCP_INFO, activeCoverage.claimCoverageCode],
    queryFn: () => pcpInfoRequest(requestData),
    enabled: Boolean(requestData?.accessToken) && Boolean(showPcpInfo),
  });
};
